import { Avatar, Box, BoxProps, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { IconEmail, IconPerson, IconPhone, IconUser } from 'assets/img';
import { getWorkerFullName } from 'shared/utils/worker';
import { HoverableBlock } from 'redesign';

type EmployeeCardProps = {
  id: string;
  firstName: string;
  infix?: string | null;
  lastName: string;
  email: string;
  phone: string | null;
  profilePictureUrl?: string;
  isOnboarded?: boolean;
  partnerName?: string;
  onClick: () => void;
};

export const EmployeeCard = (props: EmployeeCardProps) => {
  const name = getWorkerFullName(props);
  return (
    <HoverableBlock onClick={props.onClick}>
      <Card>
        <HeaderBox>
          <WrapperBox>
            <StyledAvatar src={props.profilePictureUrl}>
              <IconUser width={32} height={32} />
            </StyledAvatar>
            {props.partnerName ? (
              <Tooltip title={props.partnerName}>
                <LabelBox>{props.partnerName}</LabelBox>
              </Tooltip>
            ) : null}
          </WrapperBox>
          <Box>
            <EmployeeName>{name}</EmployeeName>
          </Box>
        </HeaderBox>

        <DetailsBox>
          <EmployeeDetailLine>
            <IconEmail />
            <EmployeeDetail>{props.email}</EmployeeDetail>
          </EmployeeDetailLine>
          <EmployeeDetailLine>
            <IconPhone />
            <EmployeeDetail>{props.phone}</EmployeeDetail>
          </EmployeeDetailLine>
          <EmployeeDetailLine>
            <IconPerson />
            <EmployeeDetail>{props.isOnboarded ? 'Onboarded' : 'Not onboarded'}</EmployeeDetail>
          </EmployeeDetailLine>
        </DetailsBox>
      </Card>
    </HoverableBlock>
  );
};

const Card = styled(Box)<BoxProps>(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'flex-start',
  display: 'inline-flex',
  padding: theme.spacing(6),
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  gap: theme.spacing(6),
  borderRadius: theme.spacing(8),
  background: theme.palette.lwBlock['default'],
  boxShadow:
    '0px 4px 6px 0px rgba(51, 51, 51, 0.03), 0px 0px 8px 0px rgba(51, 51, 51, 0.03), 0px 12px 16px 0px rgba(51, 51, 51, 0.08)',
}));

const HeaderBox = styled(Box)<BoxProps>(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 16,
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
}));

const DetailsBox = styled(Box)<BoxProps>(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  width: '100%',
  minWidth: 0,
}));

const EmployeeName = styled(Typography)(({ theme }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontFamily: 'Urbanist',
  fontSize: 16,
  fontWeight: 700,
  fontVariationSettings: '"wght" 700',
  lineHeight: 'normal',
  color: theme.palette.lwBlack[100],
}));

const StyledAvatar = styled(Avatar)<BoxProps>(({ theme }) => ({
  width: 64,
  height: 64,
  backgroundColor: theme.palette.lwPrimaryAccentLight?.[10],
}));

const EmployeeDetail = styled(Typography)(({ theme }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontFamily: 'Inter',
  fontSize: 14,
  fontWeight: 500,
  lineHeight: '20px',
  color: theme.palette.lwBlack[100],
}));

const EmployeeDetailLine = styled(Box)<BoxProps>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(2),
  overflow: 'hidden',
  minWidth: 0,
}));

const LabelBox = styled(Box)(() => ({
  border: '1px solid #eeeeee',
  borderRadius: '16px',
  backgroundColor: 'white',
  padding: '0 8px',
  marginTop: '-8px',
  lineHeight: '12px',
  fontSize: '8px',
  textAlign: 'center',
  zIndex: 100,
  textOverflow: 'ellipsis',
  overflow: 'hidden',
}));

const WrapperBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '30%',
}));
